import {reactive, watch} from 'vue'
import Debounce from '@/core/helpers/debounce'
import eventBus from "@/core/services/eventBus";

/**
 * A simple proxy/wrapper for a vuex-like reactive store, which automatically saves itself to local storage
 * when it is modified, and restores itself from local storage when the application is reloaded.
 */

// Only serialize the store to local storage once every 500ms at most.
const debouncer = new Debounce();

const store: any = reactive({
    loaded: false,
    appData: {},
    authentication: {}
});

export function safeSerialize(obj: any): string {
  const seen = new WeakSet();

  return JSON.stringify(obj, (key, value) => {
    // Skip functions, symbols, and reactive proxies
    if (typeof value === 'function' || typeof value === 'symbol') {
      return undefined;
    }

    // Skip HowlerJS instances or other complex objects
    if (value && typeof value === 'object') {
      if (seen.has(value)) {
        return undefined; // Avoid circular references
      }
      seen.add(value);

      // Skip known non-serializable types
      if (value && typeof value === 'object' && value._src && value.play) {
        return undefined; // Likely a Howl instance
      }

    }

    return value;
  });
}


// Every tab shares the one copy in localStorage but reads it only once, when it loads, and
// ARC ID refresh tokens can be used once. When another tab refreshes, the token it saves
// replaces the one this tab still holds, which is now spent -- and this tab writes its whole
// store on any change, tokens included. This is the refresh token this tab last read from or
// wrote to localStorage, so a different one there means another tab has saved since.
let refreshTokenLastSeen: string | undefined;

// Run before a deferred write, so the session another tab saved can be taken over rather than
// overwritten. Set by AuthenticationService, which knows whose session it is.
let beforeDeferredWrite: (() => void) | null = null;

// Set once this tab's session has ended while another tab's is saved: nothing this tab still
// holds may be written over it, the pending write included.
let writesStopped = false;

/**
 * The store as another tab may have saved it since this one loaded, or null when there is
 * none to read.
 */
export function readSavedStore(): any {
  try {
    return JSON.parse(localStorage.getItem('store') || 'null')?.payload ?? null;
  } catch {
    return null;
  }
}

export function lastSeenRefreshToken(): string | undefined {
  return refreshTokenLastSeen;
}

export function onBeforeDeferredWrite(hook: () => void) {
  beforeDeferredWrite = hook;
}

/**
 * Writes the store now, rather than half a second after the change.
 */
export function saveStore() {
  if (writesStopped) return;
  try {
    const serialized = safeSerialize({
      version: import.meta.env.VITE_VERSION,
      payload: store
    });
    localStorage.setItem('store', serialized);
    refreshTokenLastSeen = store.authentication?.refreshToken;
  } catch (err) {
    console.error('Failed to serialize store:', err);
  }
}

/**
 * Stops this tab writing the store at all, dropping any write still waiting. For a tab whose
 * session is over while another tab's is saved.
 */
export function stopWriting() {
  writesStopped = true;
  debouncer.cancel();
}

/**
 * Lets this tab write the store again: a sign-in here makes the saved session this tab's.
 */
export function startWriting() {
  writesStopped = false;
}

function serializeStore() {
  if (writesStopped) return;
  beforeDeferredWrite?.();
  saveStore();
}

/* When something the store is modified or added, serialize it */
watch(store, () => {
  debouncer.debounce(serializeStore);
});

/* Serialization is debounced, so a change made in the last half second before the page
 * goes away would otherwise never be written. That is long enough to lose real work:
 * change a collection setting and hit reload, and the old value comes back. Flush any
 * pending write while the page still exists.
 *
 * pagehide covers reloads, navigations and tab closes; visibilitychange covers mobile
 * browsers, which may kill a backgrounded tab without ever firing pagehide.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => debouncer.flush());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') debouncer.flush();
  });
}


/* Proxy for the reactive store, so it can be transparently saved and loaded */
const storeHandler: any = {
    getStore() {
        if (store.loaded == false) {
            const serializedStore = JSON.parse(localStorage.getItem('store') || '{}');
            Object.assign(store, serializedStore.payload);
            refreshTokenLastSeen = store.authentication?.refreshToken;
            eventBus.$emit('storeLoaded');
            store.loaded = true;
        }
        return store;
    },
    get(target, prop, receiver) {
        return Reflect.get(this.getStore(), prop, receiver);
    },
    set(target, prop, value) {
        return Reflect.set(this.getStore(), prop, value);
    }
};

const PersistentStorage = new Proxy(store, storeHandler);

export default PersistentStorage;

